import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { INDIAN_STATES, stateNameForCode } from '@/lib/indianStates';
import type { BranchDto } from '@/types/organization';

function buildSchema(t: (key: string) => string) {
  return z.object({
    branchCode: z.string().min(1, t('form.validation.branchCodeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    address: z.string().nullable(),
    status: z.enum(['Active', 'Inactive']),
    stateCode: z.string().nullable(),
    stateName: z.string().nullable(),
  });
}

export type BranchFormValues = z.infer<ReturnType<typeof buildSchema>>;

interface BranchFormDialogProps {
  open: boolean;
  branch: BranchDto | null;
  onClose: () => void;
  onSubmit: (values: BranchFormValues) => void;
  loading?: boolean;
}

export default function BranchFormDialog({ open, branch, onClose, onSubmit, loading }: BranchFormDialogProps) {
  const { t } = useTranslation('companyMaster');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset, setValue } = useForm<BranchFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { branchCode: '', name: '', address: null, status: 'Active', stateCode: null, stateName: null },
  });

  useEffect(() => {
    if (open) {
      reset(
        branch
          ? {
              branchCode: branch.branchCode, name: branch.name, address: branch.address,
              status: branch.status as 'Active' | 'Inactive', stateCode: branch.stateCode, stateName: branch.stateName,
            }
          : { branchCode: '', name: '', address: null, status: 'Active', stateCode: null, stateName: null },
      );
    }
  }, [open, branch, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{branch ? t('table.name') : t('addBranch')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="branchCode"
            control={control}
            render={({ field, fieldState }) => (
              <TextField {...field} label={t('form.branchCode')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
            )}
          />
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField {...field} label={t('form.branchName')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.address')} fullWidth multiline rows={2} />}
          />
          <Controller
            name="stateCode"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label={t('form.state')}
                fullWidth
                value={field.value ?? ''}
                onChange={(e) => {
                  const code = e.target.value || null;
                  field.onChange(code);
                  setValue('stateName', stateNameForCode(code));
                }}
              >
                <MenuItem value="">{t('form.stateNone')}</MenuItem>
                {INDIAN_STATES.map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label={t('table.status')} fullWidth>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
